import { JsonMap } from '@segment/analytics-react-native'
import * as segmentClient from './index'
import { createContext, useCallback, useContext, useMemo, useRef } from 'react'

interface ISegmentDomainProvider {
  triggerAction: (target: string, action: string, additionalProperties?: JsonMap) => Promise<void>
}

const SegmentDomainContext = createContext<ISegmentDomainProvider | null>(null)

interface ISegmentDomainProviderProps {
  domain: string
  children: React.ReactNode
  domainAdditionalProperties?: JsonMap
}

export const SegmentDomainProvider = ({
  domain,
  children,
  domainAdditionalProperties,
}: ISegmentDomainProviderProps) => {
  const domainAdditionalPropertiesRef = useRef(domainAdditionalProperties)
  const triggerAction = useCallback(
    async (target: string, action: string, additionalProperties?: JsonMap) => {
      const eventName = `${domain} ${target} ${action}`
      await segmentClient.track(eventName, {
        ...additionalProperties,
        ...(domainAdditionalPropertiesRef.current ?? {}),
      })
    },
    [domain],
  )
  domainAdditionalPropertiesRef.current = domainAdditionalProperties
  const providerValue = useMemo(() => ({ triggerAction }), [triggerAction])

  return (
    <SegmentDomainContext.Provider value={providerValue}>{children}</SegmentDomainContext.Provider>
  )
}

export const useSegmentDomain = () => {
  const context = useContext(SegmentDomainContext)
  if (!context) {
    throw new Error('useSegmentDomain must be used within a SegmentDomainProvider')
  }
  return context
}
